import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import axios from 'axios';
import { X, Edit2, RefreshCw, Trash2, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { useOverlayClose } from '../../components/useOverlayClose';
import ZoomableImage from '../../components/ZoomableImage';
import { orderedBatchPhotos } from './batchPhotos';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Table + modal CSS (self-contained) ──────────────────────────────────────

const modalCss = `
  .cdm-tbl th, .cdm-tbl td { padding: 6px 9px !important; font-size: 0.73rem !important; border: 1px solid #c5cbd3 !important; vertical-align: middle !important; white-space: nowrap; text-align: center !important; }
  .cdm-tbl th { text-transform: uppercase; letter-spacing: 0.04em; color: #1a3a5c; font-weight: 700; background: #86afd8 !important; }
  .cdm-tbl thead { background: #86afd8 !important; }
  .cdm-tbl { table-layout: fixed; width: 100% !important; }
  .cdm-tbl td.wc { white-space: normal !important; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDateTime = (d?: string | null) => {
  if (!d) return '-';
  const dt = new Date(d);
  const dd = dt.getDate().toString().padStart(2, '0');
  const mm = (dt.getMonth() + 1).toString().padStart(2, '0');
  const yy = dt.getFullYear();
  const hh = dt.getHours().toString().padStart(2, '0');
  const min = dt.getMinutes().toString().padStart(2, '0');
  return `${dd}-${mm}-${yy} ${hh}:${min}`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConsumableAdjustment {
  id: number;
  batch_id: number;
  quantity_changed: number;
  reason: string;
  created_at: string;
  user_id: number | null;
}

interface ConsumableBatch {
  id: number;
  batch_no: string | null;
  status: string;
  adjustments: ConsumableAdjustment[];
  photo_1_url: string | null;
  photo_2_url: string | null;
  photo_3_url: string | null;
  photo_4_url: string | null;
  photo_5_url: string | null;
  primary_photo_slot: number | null;
}

interface ConsumableDetail {
  id: number;
  name: string;
  gst_percent: number | null;
  low_stock_alert_quantity: number;
  is_active: boolean;
  total_stock: number;
  photo_1_url: string | null;
  photo_2_url: string | null;
  batches: ConsumableBatch[];
}

interface Consumable {
  id: number;
  name: string;
  gst_percent: number | null;
  low_stock_alert_quantity: number;
  is_active: boolean;
  total_stock: number;
}

interface EditForm {
  name: string;
  gst_percent: string;
  low_stock_alert_quantity: string;
}

interface Props {
  consumable: Consumable;
  onClose: () => void;
  onDataChange: () => void;
  /** Called after the consumable is deleted (parent should close + refresh). */
  onDeleted?: () => void;
}

// ─── Shared sub-styles ───────────────────────────────────────────────────────

const panelHeader: React.CSSProperties = {
  background: '#e8f0f7',
  borderBottom: '1px solid #d0dce8',
  padding: '0.5rem 1rem',
  fontSize: '0.67rem',
  fontWeight: 800,
  color: '#2d4a6b',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

const editInput: React.CSSProperties = {
  width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1',
  borderRadius: '6px', fontSize: '0.82rem', boxSizing: 'border-box',
  height: '34px', outline: 'none',
};

const editLabel: React.CSSProperties = {
  display: 'block', fontSize: '0.68rem', color: '#64748b', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem',
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ConsumableDetailsModal: React.FC<Props> = ({ consumable, onClose, onDataChange, onDeleted }) => {
  const [detail, setDetail]             = useState<ConsumableDetail | null>(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [isEditing, setIsEditing]       = useState(false);
  const [isSaving, setIsSaving]         = useState(false);
  const [isDeleting, setIsDeleting]     = useState(false);
  const [editForm, setEditForm]         = useState<EditForm>({ name: '', gst_percent: '', low_stock_alert_quantity: '' });
  const [settingPrimary, setSettingPrimary] = useState(false);
  const overlayClose                    = useOverlayClose(onClose);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get<ConsumableDetail>(
        `${API_URL}/api/consumables/${consumable.id}`,
        { headers: getHeaders() }
      );
      setDetail(res.data);
    } catch {
      toast.error('Failed to load consumable details.');
    } finally {
      setIsLoading(false);
    }
  }, [consumable.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const startEditing = () => {
    if (!detail) return;
    setEditForm({
      name:                    detail.name,
      gst_percent:             detail.gst_percent != null ? String(detail.gst_percent) : '',
      low_stock_alert_quantity: String(detail.low_stock_alert_quantity),
    });
    setIsEditing(true);
  };

  const cancelEditing = () => setIsEditing(false);

  const handleEditChange = (e: ChangeEvent<HTMLInputElement>) =>
    setEditForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!editForm.name.trim()) { toast.error('Name is required.'); return; }
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('consumable_data', JSON.stringify({
        name:                     editForm.name.trim(),
        gst_percent:              editForm.gst_percent ? parseFloat(editForm.gst_percent) : null,
        low_stock_alert_quantity: parseInt(editForm.low_stock_alert_quantity) || 10,
      }));
      await axios.put(
        `${API_URL}/api/consumables/${consumable.id}`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Catalog details updated.');
      setIsEditing(false);
      await fetchDetail();
      onDataChange();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const setPrimary = async (batchId: number, slot: number) => {
    if (settingPrimary) return;
    setSettingPrimary(true);
    try {
      const formData = new FormData();
      formData.append('batch_data', JSON.stringify({ primary_photo_slot: slot }));
      await axios.put(
        `${API_URL}/api/consumables/batches/${batchId}`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Display photo updated.');
      await fetchDetail();
      onDataChange();
    } catch (err: any) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update display photo.');
    } finally {
      setSettingPrimary(false);
    }
  };

  const handleDelete = async () => {
    const name = detail?.name ?? consumable.name;
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/consumables/${consumable.id}`, { headers: getHeaders() });
      toast.success('Consumable deleted successfully');
      (onDeleted ?? onClose)();
    } catch {
      toast.error('Failed to delete consumable.');
      setIsDeleting(false);
    }
  };

  const INVOICE_KEYWORDS = ['invoice', 'auto deduct', 'auto refund'];
  const allAdjustments = (detail?.batches ?? [])
    .flatMap(b => b.adjustments.map(a => ({ ...a, batch_no: b.batch_no })))
    .filter(a => {
      const r = a.reason.toLowerCase();
      return !INVOICE_KEYWORDS.some(kw => r.includes(kw));
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <>
      <style>{modalCss}</style>

      {/* ── Overlay ── */}
      <div
        {...overlayClose}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
      >
        {/* ── Modal wrapper ── */}
        <div
          onClick={e => e.stopPropagation()}
          style={{ backgroundColor: 'white', borderRadius: '12px', width: '96%', maxWidth: '880px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
        >
          {/* ── Header ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 1.25rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, gap: '1rem' }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Consumable Details</p>
              <h2 style={{ margin: '0.1rem 0 0', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {detail?.name ?? consumable.name}
              </h2>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
              <button onClick={fetchDetail} title="Refresh" style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', padding: '0.38rem', display: 'flex', color: '#64748b' }}>
                <RefreshCw size={14} />
              </button>
              {!isEditing && (
                <button
                  onClick={startEditing}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.38rem 0.85rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}
                >
                  <Edit2 size={13} /> Edit Details
                </button>
              )}
              {isEditing && (
                <>
                  <button onClick={cancelEditing} style={{ padding: '0.38rem 0.85rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{ padding: '0.38rem 0.85rem', background: isSaving ? '#94a3b8' : '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 700 }}
                  >
                    {isSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </>
              )}
              <button onClick={onClose} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', padding: '0.38rem', display: 'flex', color: '#64748b' }}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

            {isLoading ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem', fontSize: '0.9rem' }}>Loading…</p>
            ) : detail ? (
              <>
                {/* ── Catalog Details (edit form only — GST/Low Alert/Total already shown in the main table) ── */}
                {isEditing && (
                  <div style={{ borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                    <div style={panelHeader}>Catalog Details</div>
                    <div style={{ padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                      <div>
                        <label style={editLabel}>Name <span style={{ color: '#ef4444' }}>*</span></label>
                        <input name="name" value={editForm.name} onChange={handleEditChange} style={editInput} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                        <div>
                          <label style={editLabel}>GST %</label>
                          <input name="gst_percent" type="number" min="0" max="100" step="0.01" value={editForm.gst_percent} onChange={handleEditChange} placeholder="e.g. 12" style={editInput} />
                        </div>
                        <div>
                          <label style={editLabel}>Low Stock Alert Qty</label>
                          <input name="low_stock_alert_quantity" type="number" min="0" value={editForm.low_stock_alert_quantity} onChange={handleEditChange} style={editInput} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Batch Photos Section ── */}
                <div style={{ borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                  <div style={panelHeader}>Batch Photos</div>
                  {detail.batches.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '0.65rem 1rem', margin: 0, textAlign: 'center' }}>
                      No batches recorded yet.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', padding: '0.75rem 1rem', maxHeight: '30vh', overflowY: 'auto' }}>
                      {detail.batches.map(b => {
                        const photos = orderedBatchPhotos(b);
                        return (
                          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                            <span style={{ minWidth: 92, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                              {b.batch_no || `#${b.id}`}
                            </span>
                            {photos.length === 0 ? (
                              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>No photos</span>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                {photos.map(({ slot, url, isPrimary }) => (
                                  <div key={slot} style={{ position: 'relative', width: 48, height: 48 }}>
                                    <ZoomableImage
                                      src={url}
                                      alt={`${b.batch_no || 'Batch'} photo ${slot}`}
                                      style={{ width: 48, height: 48, borderRadius: '6px', objectFit: 'cover', border: isPrimary ? '2px solid #f59e0b' : '1px solid #e2e8f0' }}
                                    />
                                    <button
                                      type="button"
                                      onClick={e => { e.stopPropagation(); if (!isPrimary) setPrimary(b.id, slot); }}
                                      disabled={settingPrimary}
                                      title={isPrimary ? 'Display photo' : 'Set as display photo'}
                                      style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'white', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.15)', cursor: settingPrimary ? 'not-allowed' : 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      <Star size={12} color="#f59e0b" fill={isPrimary ? '#f59e0b' : 'none'} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Adjustment History Section ── */}
                <div style={{ flexShrink: 0 }}>
                  <div style={panelHeader}>Adjustment History</div>
                  {allAdjustments.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '0.65rem 1rem', margin: 0, textAlign: 'center' }}>
                      No adjustments recorded yet.
                    </p>
                  ) : (
                    <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                      <table className="cdm-tbl">
                        <thead>
                          <tr>
                            <th style={{ width: '16%' }}>Date / Time</th>
                            <th style={{ width: '12%' }}>Batch</th>
                            <th style={{ width: '10%' }}>Qty Change</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allAdjustments.map(a => (
                            <tr key={a.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.created_at)}</td>
                              <td>{a.batch_no || `#${a.batch_id}`}</td>
                              <td style={{ fontWeight: 700, color: a.quantity_changed < 0 ? '#dc2626' : '#15803d' }}>
                                {a.quantity_changed > 0 ? '+' : ''}{a.quantity_changed}
                              </td>
                              <td className="wc" style={{ textAlign: 'left' }}>{a.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              </>
            ) : null}
          </div>

          {/* ── Footer (Delete) ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.75rem 1.25rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
              Deleting removes this consumable and its batches from the catalog.
            </span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.9rem', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', cursor: isDeleting ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}
            >
              <Trash2 size={13} /> {isDeleting ? 'Deleting…' : 'Delete Consumable'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsumableDetailsModal;
